import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled/macro';
import { useTranslation } from 'react-i18next';
import { Colors } from '@robinpowered/design-system';

type SignatureCaptureProps = {
  /**
   * Called with a PNG data URI of the drawn signature whenever a stroke is
   * completed, and with null when the pad is empty or cleared.
   */
  onSignatureChange: (dataUrl: string | null) => void;
};

const STROKE_COLOR = Colors.Gray100;
const STROKE_WIDTH = 2.5;

/**
 * A draw-to-sign pad. Visitors sign with their finger (or mouse); the parent
 * receives the signature as a PNG data URI to upload when agreeing.
 */
export const SignatureCapture = ({
  onSignatureChange,
}: SignatureCaptureProps): JSX.Element => {
  const { t } = useTranslation('documentAgreement');
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);
  const hasStrokesRef = useRef(false);
  const [hasStrokes, setHasStrokes] = useState(false);

  const getContext = () => canvasRef.current?.getContext('2d') ?? null;

  // The visible canvas stays transparent so the pad's gray background shows
  // through. Export composites the strokes onto white rather than
  // transparent/gray so the signature stays legible wherever the uploaded
  // image is rendered.
  const exportSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return null;
    }
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = canvas.width;
    exportCanvas.height = canvas.height;
    const ctx = exportCanvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    ctx.fillStyle = Colors.White0;
    ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    ctx.drawImage(canvas, 0, 0);
    return exportCanvas.toDataURL('image/png');
  };

  // Size the canvas backing store to the rendered element, accounting for
  // device pixel ratio so strokes stay crisp on retina kiosk screens.
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = getContext();
    if (!canvas || !ctx) {
      return;
    }
    const { width, height } = canvas.getBoundingClientRect();
    const scale = window.devicePixelRatio || 1;
    canvas.width = width * scale;
    canvas.height = height * scale;
    ctx.scale(scale, scale);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = STROKE_COLOR;
    ctx.lineWidth = STROKE_WIDTH;
  }, []);

  const pointerPosition = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = getContext();
    if (!ctx) {
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    const { x, y } = pointerPosition(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
    // Draw a dot so a single tap still leaves a mark.
    ctx.lineTo(x, y);
    ctx.stroke();
    isDrawingRef.current = true;
    hasStrokesRef.current = true;
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = getContext();
    if (!ctx || !isDrawingRef.current) {
      return;
    }
    const { x, y } = pointerPosition(event);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) {
      return;
    }
    isDrawingRef.current = false;
    event.currentTarget.releasePointerCapture(event.pointerId);
    setHasStrokes(hasStrokesRef.current);
    if (hasStrokesRef.current) {
      onSignatureChange(exportSignature());
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = getContext();
    if (!canvas || !ctx) {
      return;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    isDrawingRef.current = false;
    hasStrokesRef.current = false;
    setHasStrokes(false);
    onSignatureChange(null);
  };

  return (
    <SignaturePad>
      <SignatureCanvas
        ref={canvasRef}
        aria-label={t('signature_input_label')}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      />
      <SignatureBaseline>
        <ClearButton
          type="button"
          aria-label={t('clear_signature')}
          onClick={clearSignature}
          disabled={!hasStrokes}
        >
          ×
        </ClearButton>
        <BaselineRule />
      </SignatureBaseline>
    </SignaturePad>
  );
};

const SignaturePad = styled.div`
  position: relative;
  width: 100%;
  height: 164px;
  background-color: ${Colors.Gray5};
  border: 1px solid ${Colors.Tan70};
  border-radius: 8px;
  overflow: hidden;
`;

const SignatureCanvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  /* Draw with a finger without scrolling the page on touch screens */
  touch-action: none;
`;

const SignatureBaseline = styled.div`
  position: absolute;
  left: 20px;
  right: 20px;
  bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  /* The baseline is decorative; strokes land on the canvas above it */
  pointer-events: none;
`;

const ClearButton = styled.button`
  pointer-events: auto;
  background: none;
  border: none;
  padding: 0 4px;
  font-size: 20px;
  line-height: 1;
  color: ${Colors.Gray40};
  cursor: pointer;

  &:disabled {
    cursor: default;
  }

  &:focus {
    /* A11y on focus */
    outline: 2px solid ${Colors.Magenta100};
    border-radius: 4px;
  }
`;

const BaselineRule = styled.div`
  flex: 1;
  border-bottom: 1px solid ${Colors.Gray40};
`;
